import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag, ExternalLink, Check, X, Trash2, Ban, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { REASONS } from "@/components/public/ReportButton";

const REASON_LABEL = Object.fromEntries(REASONS);
const TYPE_LABEL = { comment: "News comment", game_comment: "Game comment", stream_comment: "Live chat", stream: "Stream", profile: "Profile" };
const TABS = [["open", "Open"], ["resolved", "Resolved"], ["dismissed", "Dismissed"]];
const DURATIONS = [[7, "7d"], [30, "30d"], [0, "Perm"]];

function Actions({ r, onDone }) {
  const [busy, setBusy] = useState("");
  const [days, setDays] = useState(7);
  const act = async (action) => {
    if (action === "suspend" && !window.confirm(`Suspend ${r.target_user_name} (${days ? `${days} days` : "permanent"})?`)) return;
    if (action === "delete_content" && !window.confirm("Remove this content?")) return;
    setBusy(action);
    try { await api.post(`/admin/reports/${r.id}/resolve`, { action, days }); toast.success("Report updated"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const btn = "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50";
  const spin = (a, Icon) => (busy === a ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {r.target_type !== "profile" && <button onClick={() => act("delete_content")} disabled={!!busy} data-testid={`report-delete-content-${r.id}`} className={`${btn} text-red-400 hover:bg-red-500/10`}>{spin("delete_content", Trash2)} {r.target_type === "stream" ? "End stream" : "Delete content"}</button>}
      <span className="inline-flex items-center rounded-lg border border-white/10 overflow-hidden">
        {DURATIONS.map(([d, l]) => <button key={d} onClick={() => setDays(d)} data-testid={`report-days-${d}-${r.id}`} className={`px-2 py-1.5 text-[11px] font-bold ${days === d ? "bg-amber-500/20 text-amber-300" : "text-white/50"}`}>{l}</button>)}
        <button onClick={() => act("suspend")} disabled={!!busy} data-testid={`report-suspend-${r.id}`} className={`${btn} rounded-none text-amber-400 hover:bg-amber-500/10`}>{spin("suspend", Ban)} Suspend</button>
      </span>
      <button onClick={() => act("resolve")} disabled={!!busy} data-testid={`report-resolve-${r.id}`} className={`${btn} text-emerald-400 hover:bg-emerald-500/10`}>{spin("resolve", Check)} Mark resolved</button>
      <button onClick={() => act("dismiss")} disabled={!!busy} data-testid={`report-dismiss-${r.id}`} className={`${btn} text-white/60 hover:bg-white/10`}>{spin("dismiss", X)} Dismiss</button>
    </div>
  );
}

function ReportCard({ r, onDone }) {
  return (
    <div className="card-surface rounded-xl p-4" data-testid={`admin-report-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 font-bold">{REASON_LABEL[r.reason]}</span>
        <span className="px-2 py-0.5 rounded bg-white/5 text-white/60 font-semibold">{TYPE_LABEL[r.target_type]}</span>
        <span className="text-white/40">by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}</span>
        {r.status !== "open" && <span className="ml-auto text-white/40 capitalize" data-testid={`report-status-${r.id}`}>{r.status} · {r.action?.replace("_", " ")}</span>}
      </div>
      <div className="mt-2 text-sm text-white"><span className="font-semibold">{r.target_user_name}</span> <span className="text-white/40">{r.target_user_email}</span></div>
      {r.snapshot && <p className="mt-1.5 text-sm text-white/75 whitespace-pre-line break-words border-l-2 border-white/10 pl-3">{r.snapshot}</p>}
      {r.note && <p className="mt-2 text-xs text-white/50 italic">Reporter note: "{r.note}"</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <a href={r.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))]" data-testid={`report-link-${r.id}`}><ExternalLink size={11} /> View in context</a>
        {r.status === "open" && <Actions r={r} onDone={onDone} />}
      </div>
    </div>
  );
}

export default function AdminReports() {
  const [tab, setTab] = useState("open");
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/reports", { params: { status: tab } }).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} })), [tab]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-reports">
      <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Flag size={20} className="text-amber-400" /> Reports</h1>
      <p className="text-sm text-white/50">Violations reported by members on comments, live streams and profiles.</p>
      <div className="mt-5 mb-4 flex gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`reports-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-[hsl(var(--primary))] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}>
            {l} <span className="opacity-70" data-testid={`reports-count-${k}`}>{data?.counts?.[k] ?? 0}</span>
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {data === null ? <Loader2 className="animate-spin text-white/30" /> : data.items.map((r) => <ReportCard key={r.id} r={r} onDone={load} />)}
        {data?.items.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-white/40" data-testid="reports-empty">No {tab} reports.</div>}
      </div>
    </div>
  );
}
